import Ionicons from "@expo/vector-icons/Ionicons";
import type { ComponentProps, ReactNode } from "react";
import { useRouter } from "expo-router";
import { Pressable, StyleSheet, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { KeyboardScrollView } from "@/components/ui/keyboard-view";
import { useTranslation } from "react-i18next";

import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import i18n from "@/i18n";

type IoniconName = ComponentProps<typeof Ionicons>["name"];

/** Screen scaffold: back header + centered, scrollable content column. */
export function HelpScreen({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  useTranslation();
  const c = useTheme();
  const router = useRouter();

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      <View style={[styles.header, { borderBottomColor: c.border }]}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={i18n.t("help.common.goBackA11y")}
          onPress={() => router.back()}
          hitSlop={10}
          style={({ pressed }) => [
            styles.backButton,
            pressed && styles.pressed,
          ]}
        >
          <Ionicons name="chevron-back" size={23} color={c.tint} />
        </Pressable>

        <ThemedText
          variant="bodyStrong"
          style={styles.headerTitle}
          numberOfLines={1}
        >
          {title}
        </ThemedText>

        <View style={styles.headerSpacer} />
      </View>

      <KeyboardScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.content}
      >
        {children}
      </KeyboardScrollView>
    </SafeAreaView>
  );
}

/** Lead paragraph under the title. */
export function HelpIntro({ children }: { children: ReactNode }) {
  return (
    <ThemedText variant="body" tone="secondary" style={styles.intro}>
      {children}
    </ThemedText>
  );
}

/** A titled block with optional body copy and a bulleted list. */
export function HelpSection({
  heading,
  body,
  bullets,
  children,
}: {
  heading?: string;
  body?: string;
  bullets?: string[];
  children?: ReactNode;
}) {
  return (
    <View style={styles.section}>
      {heading ? <ThemedText variant="subtitle">{heading}</ThemedText> : null}
      {body ? (
        <ThemedText variant="body" tone="secondary">
          {body}
        </ThemedText>
      ) : null}
      {bullets ? <Bullets items={bullets} /> : null}
      {children}
    </View>
  );
}

export function Bullets({ items }: { items: string[] }) {
  const c = useTheme();
  return (
    <View style={styles.bullets}>
      {items.map((item) => (
        <View key={item} style={styles.bulletRow}>
          <View style={[styles.bulletDot, { backgroundColor: c.tint }]} />
          <ThemedText variant="body" tone="secondary" style={styles.bulletText}>
            {item}
          </ThemedText>
        </View>
      ))}
    </View>
  );
}

/** A soft callout box for reminders / guidance. */
export function InfoNote({
  icon = "information-circle-outline",
  children,
}: {
  icon?: IoniconName;
  children: ReactNode;
}) {
  const c = useTheme();
  return (
    <View
      style={[
        styles.note,
        { backgroundColor: c.surfaceAlt, borderColor: c.border },
      ]}
    >
      <Ionicons name={icon} size={18} color={c.textSecondary} />
      <ThemedText variant="caption" tone="secondary" style={styles.noteText}>
        {children}
      </ThemedText>
    </View>
  );
}

export function HelpButton({
  label,
  icon,
  onPress,
  variant = "primary",
  disabled,
}: {
  label: string;
  icon?: IoniconName;
  onPress: () => void;
  variant?: "primary" | "secondary" | "danger";
  disabled?: boolean;
}) {
  const c = useTheme();
  const isPrimary = variant === "primary";
  const isDanger = variant === "danger";
  const bg = isPrimary ? c.tint : isDanger ? c.dangerTint : c.surfaceAlt;
  const fg = isPrimary ? c.onTint : isDanger ? c.danger : c.tint;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => [
        styles.button,
        { backgroundColor: bg, borderColor: isPrimary ? bg : c.border },
        disabled && styles.buttonDisabled,
        pressed && !disabled && styles.pressed,
      ]}
    >
      {icon ? <Ionicons name={icon} size={18} color={fg} /> : null}
      <ThemedText variant="bodyStrong" style={{ color: fg }}>
        {label}
      </ThemedText>
    </Pressable>
  );
}

/** A tappable row for the Help & Support menu. */
export function MenuRow({
  icon,
  title,
  subtitle,
  onPress,
}: {
  icon: IoniconName;
  title: string;
  subtitle: string;
  onPress: () => void;
}) {
  const c = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      onPress={onPress}
      style={({ pressed }) => [
        styles.menuRow,
        { backgroundColor: c.surface, borderColor: c.border },
        pressed && styles.pressed,
      ]}
    >
      <View style={[styles.menuIcon, { backgroundColor: c.surfaceAlt }]}>
        <Ionicons name={icon} size={20} color={c.tint} />
      </View>
      <View style={styles.menuCopy}>
        <ThemedText variant="bodyStrong">{title}</ThemedText>
        <ThemedText variant="caption" tone="secondary">
          {subtitle}
        </ThemedText>
      </View>
      <Ionicons name="chevron-forward" size={20} color={c.textMuted} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    minHeight: 56,
    paddingHorizontal: Spacing.md,
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  pressed: { opacity: 0.72 },
  headerTitle: { flex: 1, textAlign: "center" },
  headerSpacer: { width: 40 },
  content: {
    width: "100%",
    maxWidth: 760,
    alignSelf: "center",
    gap: Spacing.lg,
    padding: Spacing.lg,
    paddingBottom: Spacing.xxxl,
  },
  intro: { marginBottom: Spacing.xs },
  section: { gap: Spacing.sm },
  bullets: { gap: Spacing.sm, marginTop: Spacing.xs },
  bulletRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: Spacing.sm,
  },
  bulletDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginTop: 8,
  },
  bulletText: { flex: 1 },
  note: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: Spacing.sm,
    borderWidth: 1,
    borderRadius: Radius.lg,
    padding: Spacing.md,
  },
  noteText: { flex: 1 },
  button: {
    minHeight: 50,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: 1,
    paddingHorizontal: Spacing.lg,
  },
  buttonDisabled: { opacity: 0.5 },
  menuRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    borderWidth: 1,
    borderRadius: Radius.lg,
    padding: Spacing.md,
  },
  menuIcon: {
    width: 40,
    height: 40,
    borderRadius: Radius.md,
    alignItems: "center",
    justifyContent: "center",
  },
  menuCopy: { flex: 1, gap: 2 },
});
